/**
 * Design System — shared sx objects and tokens
 * Import named exports and spread into sx props.
 */

// ── Brand tokens ──────────────────────────────────────────────────────────────
export const NAVY      = '#0d1d3e';
export const NAVY_HOVER = '#162d5a';
export const GOLD      = '#c8a96e';
export const GOLD_HOVER = '#b8935a';
/**
 * A darkened GOLD for text/icon-outline use on light backgrounds. Plain
 * GOLD text on white measures ~2.2:1 contrast — well under WCAG AA's 3:1
 * minimum even for large text. This shade measures ~4.9:1 on white (and
 * comfortably passes the large-text 3:1 threshold on GOLD-tinted
 * backgrounds, e.g. an avatar). GOLD itself is unchanged and still used
 * for backgrounds/accents/icons-on-dark, where it already passes contrast.
 */
export const GOLD_TEXT = '#826e47';
export const BG        = '#faf8f5';
export const STRIPE    = '#faf8f5'; // alternate table row

// ── Accessibility: keyboard support for a clickable row/card ──────────────────
/**
 * Spread onto a TableRow/Card whose only interaction is an onClick handler —
 * plain onClick is mouse-only: a <tr>/<div> is never in the native tab order
 * and never responds to Enter/Space, so keyboard-only users cannot reach or
 * activate it at all today.
 *
 * `asButton: true` (for a clickable Card, not inside a table) also sets
 * role="button" and accepts Space as well as Enter — the fully-correct ARIA
 * pattern for a clickable div with no competing semantics.
 *
 * `asButton: false` (the default, for a clickable TableRow) deliberately
 * does NOT override role — a <tr> already has an implicit, meaningful "row"
 * role inside its table, and replacing it with "button" would orphan its
 * <TableCell> children from assistive tech's table-navigation model. Only
 * Enter activates it (Space is left alone, since it's the native page-scroll
 * key and this element still reads as a row, not a button).
 *
 * Checks `e.target === e.currentTarget` either way, so a keypress on a
 * nested interactive child (e.g. a "receive" button inside the row) — which
 * already calls stopPropagation() for mouse clicks — doesn't also trigger
 * the row's own action via bubbling.
 */
export function clickableA11y(onActivate, { ariaLabel, asButton = false } = {}) {
  const base = {
    tabIndex: 0,
    'aria-label': ariaLabel,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      const isActivateKey = asButton ? (e.key === 'Enter' || e.key === ' ') : e.key === 'Enter';
      if (isActivateKey) { e.preventDefault(); onActivate(e); }
    },
  };
  return asButton ? { ...base, role: 'button' } : base;
}

// ── Quantity formatter ────────────────────────────────────────────────────────
// Shows integers without decimals, shows decimals only when meaningful
export const fmtQty = (qty, unit) => {
  const n = Number(qty ?? 0);
  const hasDecimals = Math.abs(n - Math.round(n)) > 0.0005;
  const str = hasDecimals
    ? n.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : Math.round(n).toLocaleString('he-IL');
  if (!unit || unit === 'יחידה') return str;
  return `${str} ${unit}`;
};

// ── Card variants ─────────────────────────────────────────────────────────────
export const card = {
  /** Standard card */
  base: {
    borderRadius: 2,
    boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
  },
  /** Clickable card with lift on hover */
  clickable: {
    borderRadius: 2,
    cursor: 'pointer',
    transition: '0.2s',
    boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    },
  },
  /** Section card inside a page (has margin-bottom) */
  section: {
    borderRadius: 2,
    mb: 3,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  /** Highlighted/bordered card (e.g. inline form) */
  highlight: {
    mb: 2,
    borderRadius: 2,
    border: `1.5px solid rgba(200,169,110,0.5)`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  /** Edit-mode highlighted card */
  editHighlight: {
    mb: 2,
    borderRadius: 2,
    border: `1.5px solid rgba(13,29,62,0.3)`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
};

// ── Button variants (use as sx on MUI Button) ─────────────────────────────────
export const btn = {
  /** Primary filled — navy */
  primary: {
    backgroundColor: NAVY,
    borderRadius: 2,
    '&:hover': { backgroundColor: NAVY_HOVER },
  },
  /** Primary filled — navy, wider padding */
  primaryLg: {
    backgroundColor: NAVY,
    borderRadius: 2,
    px: 4,
    '&:hover': { backgroundColor: NAVY_HOVER },
  },
  /** Outlined gold — for secondary actions. Uses GOLD_TEXT, not GOLD
   *  itself, for the text/border color — plain GOLD measures ~2.2:1
   *  against white, under WCAG's 3:1 minimum even for a border. */
  gold: {
    borderColor: GOLD_TEXT,
    color: GOLD_TEXT,
    borderRadius: 2,
    '&:hover': {
      borderColor: GOLD_HOVER,
      backgroundColor: 'rgba(200,169,110,0.08)',
    },
  },
  /** Outlined grey — for cancel/back */
  cancel: {
    borderColor: '#ccc',
    color: '#666',
    borderRadius: 2,
  },
  /** TopToolbar create button */
  create: {
    backgroundColor: NAVY,
    color: '#fff',
    borderRadius: 2,
    px: 2,
    '&:hover': { backgroundColor: NAVY_HOVER },
  },
};

// ── Table helpers ─────────────────────────────────────────────────────────────
export const tbl = {
  /** sx for the outer container Box/TableContainer */
  container: {
    borderRadius: 2,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    '@media (max-width:900px)': {
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
    },
  },
  /** sx on TableRow inside TableHead */
  headerRow: {
    backgroundColor: NAVY,
  },
  /** sx on TableCell inside TableHead (color/weight handled by theme) */
  headerCell: {
    color: '#fff',
    fontWeight: 700,
  },
  /** Returns sx for a clickable body TableRow given row index */
  bodyRow: (i) => ({
    cursor: 'pointer',
    backgroundColor: i % 2 === 0 ? '#fff' : STRIPE,
    '&:hover': { backgroundColor: 'rgba(13,29,62,0.05)' },
  }),
  /** Non-clickable zebra row */
  zebraRow: (i) => ({
    backgroundColor: i % 2 === 0 ? '#fff' : STRIPE,
  }),
  /** Transparent list container — removes react-admin box-shadow */
  listSx: {
    '& .RaList-content': { boxShadow: 'none', backgroundColor: 'transparent' },
  },
};

// ── Form / Input ──────────────────────────────────────────────────────────────
export const inputSx = {
  backgroundColor: '#fff',
  borderRadius: 1,
  '& .MuiOutlinedInput-root': { borderRadius: 1 },
};

// ── Status metadata ───────────────────────────────────────────────────────────
export const ORDER_STATUS = {
  pending:    { bg: '#FFF3CD', color: '#856404', label: 'ממתין'  },
  processing: { bg: '#CCE5FF', color: '#004085', label: 'בטיפול' },
  shipped:    { bg: '#D4EDDA', color: '#155724', label: 'נשלח'   },
  delivered:  { bg: '#D1ECF1', color: '#0C5460', label: 'נמסר'   },
  cancelled:  { bg: '#F8D7DA', color: '#721C24', label: 'בוטל'   },
};

export const PAYMENT_STATUS = {
  pending:  { bg: '#FFF3CD', color: '#856404', label: 'ממתין'  },
  paid:     { bg: '#D4EDDA', color: '#155724', label: 'שולם'   },
  failed:   { bg: '#F8D7DA', color: '#721C24', label: 'נכשל'   },
  refunded: { bg: '#E2E3E5', color: '#383D41', label: 'הוחזר'  },
};

export const ORDER_PAYMENT_STATUS = {
  unpaid:         { bg: '#F8D7DA', color: '#721C24', label: 'לא שולם'     },
  partially_paid: { bg: '#CCE5FF', color: '#004085', label: 'שולם חלקית'  },
  paid:           { bg: '#D4EDDA', color: '#155724', label: 'שולם במלואו' },
  failed:         { bg: '#F8D7DA', color: '#721C24', label: 'נכשל'         },
  refunded:       { bg: '#E2E3E5', color: '#383D41', label: 'הוחזר'        },
};

export const LEAD_STATUS = {
  new:        { bg: '#CCE5FF', color: '#004085', label: 'חדש'    },
  contacted:  { bg: '#FFF3CD', color: '#856404', label: 'טופל'   },
  qualified:  { bg: '#D4EDDA', color: '#155724', label: 'מתאים'  },
  closed:     { bg: '#E2E3E5', color: '#383D41', label: 'סגור'   },
  converted:  { bg: '#D4EDDA', color: '#155724', label: 'הומר ללקוח' },
};

export const PURCHASE_STATUS = {
  ordered:   { bg: '#FFF3CD', color: '#856404', label: 'הוזמן'  },
  received:  { bg: '#D4EDDA', color: '#155724', label: 'התקבל' },
  cancelled: { bg: '#F8D7DA', color: '#721C24', label: 'בוטל'   },
};

export const PRODUCTION_BATCH_STATUS = {
  draft:     { bg: '#FFF3CD', color: '#856404', label: 'טיוטה' },
  confirmed: { bg: '#D4EDDA', color: '#155724', label: 'אושר'  },
  cancelled: { bg: '#F8D7DA', color: '#721C24', label: 'בוטל'  },
};

export const PAYMENT_METHOD_LABELS = {
  bank_transfer: 'העברה בנקאית',
  credit_card:   'כרטיס אשראי',
  paypal:        'PayPal',
  cash:          'מזומן',
  check:         "צ'ק",
  zelle:         'Zelle',
};

// ── Customer website orders (docs/website/) ───────────────────────────────────
// Values must stay in sync with backend/constants/statuses.js (ORDER_ORIGINS,
// WEBSITE_APPROVAL_STATUSES, WEBSITE_SHIPPING_REVIEW_STATES).
export const ORDER_ORIGIN = {
  crm:     { bg: '#E2E3E5', color: '#383D41', label: 'CRM'  },
  website: { bg: '#FFF3CD', color: '#856404', label: 'אתר'  },
};

export const WEBSITE_APPROVAL_STATUS = {
  pending:             { bg: '#CCE5FF', color: '#004085', label: 'ממתינה לאישור'    },
  approved:            { bg: '#D4EDDA', color: '#155724', label: 'אושרה'            },
  reapproval_required: { bg: '#FFF3CD', color: '#856404', label: 'נדרש אישור מחדש'  },
  rejected:            { bg: '#F8D7DA', color: '#721C24', label: 'נדחתה'            },
};

export const WEBSITE_SHIPPING_REVIEW_STATE = {
  not_required: { bg: '#E2E3E5', color: '#383D41', label: 'משלוח מחושב'      },
  required:     { bg: '#F8D7DA', color: '#721C24', label: 'משלוח טעון בדיקה' },
  resolved:     { bg: '#D4EDDA', color: '#155724', label: 'משלוח נקבע'       },
};

// Mirrors WEBSITE_PAYMENT_REQUEST_STATUSES (backend/constants/statuses.js).
export const WEBSITE_PAYMENT_REQUEST_STATUS = {
  not_requested: { bg: '#E2E3E5', color: '#383D41', label: 'טרם נשלחה בקשת תשלום' },
  requested:     { bg: '#CCE5FF', color: '#004085', label: 'נשלחה בקשת תשלום'     },
  superseded:    { bg: '#FFF3CD', color: '#856404', label: 'בקשת התשלום בוטלה'    },
};

// ── Customer website catalog (docs/website/ Phase C) ─────────────────────────
// Values must stay in sync with backend/constants/statuses.js
// (WEBSITE_PUBLICATION_STATES, WEBSITE_PRODUCT_FAMILIES, WEBSITE_AVAILABILITY,
// WEBSITE_MAPPING_REVIEW_STATES) and the issue types / validation codes in
// backend/services/websiteCatalogService.js.
// Customer source (CUSTOMER_SOURCES in backend/constants/statuses.js; derived, R3).
export const CUSTOMER_SOURCE = {
  crm:     { bg: '#E2E3E5', color: '#383D41', label: 'CRM'        },
  website: { bg: '#D1ECF1', color: '#0C5460', label: 'אתר'        },
  both:    { bg: '#D4EDDA', color: '#155724', label: 'CRM + אתר' },
};

// Website account ↔ CRM customer link (WEBSITE_LINK_STATES in backend/constants/statuses.js).
export const WEBSITE_LINK_STATE = {
  linked: { bg: '#D4EDDA', color: '#155724', label: 'מקושר'      },
  review: { bg: '#FFF3CD', color: '#856404', label: 'לבדיקה'     },
  none:   { bg: '#E2E3E5', color: '#383D41', label: 'לא מקושר'   },
};

// Coupons (WD-006) — mirror WEBSITE_COUPON_KINDS / _ELIGIBILITY in backend/constants/statuses.js.
export const WEBSITE_COUPON_KIND = {
  percent:       { bg: '#E8F0FE', color: '#1A4FA0', label: 'אחוז הנחה'   },
  fixed:         { bg: '#EDE7F6', color: '#4A2C8A', label: 'סכום הנחה'   },
  free_shipping: { bg: '#D4EDDA', color: '#155724', label: 'משלוח חינם'  },
};
export const WEBSITE_COUPON_ELIGIBILITY = {
  everyone: { label: 'כל מי שיש לו את הקוד' },
  shluchim: { label: 'שלוחים מאושרים בלבד' },
};

export const WEBSITE_PUBLICATION_STATE = {
  draft:       { bg: '#FFF3CD', color: '#856404', label: 'טיוטה'      },
  published:   { bg: '#D4EDDA', color: '#155724', label: 'מפורסם'     },
  unpublished: { bg: '#E2E3E5', color: '#383D41', label: 'לא מפורסם'  },
  retired:     { bg: '#F8D7DA', color: '#721C24', label: 'הוצא משימוש' },
};

export const WEBSITE_FAMILY_LABELS = {
  simple_tefillin: 'תפילין פשוטות',
  gasot_tefillin:  'תפילין גסות',
  mezuzah:         'מזוזה',
  megillah:        'מגילה',
  price_on_request: 'מחיר לפי פנייה (ספר תורה)',
  other:           'אחר (לא ניתן לפרסום)',
};

export const WEBSITE_AVAILABILITY_LABELS = {
  available:   'זמין',
  unavailable: 'לא זמין',
};

export const WEBSITE_MAPPING_REVIEW_STATE = {
  clear:    { bg: '#D4EDDA', color: '#155724', label: 'מיפוי תקין'      },
  conflict: { bg: '#F8D7DA', color: '#721C24', label: 'קונפליקט מיפוי' },
};

export const WEBSITE_MAPPING_ISSUE_TYPE_LABELS = {
  multiple_sizes_for_simple_script: 'וריאנטים בכמה מידות לכתב אחד (תפילין פשוטות)',
  unverifiable_variant_size:        'וריאנט CRM ללא מידה',
};

export const WEBSITE_ISSUE_STATUS = {
  open:     { bg: '#F8D7DA', color: '#721C24', label: 'פתוחה' },
  resolved: { bg: '#D4EDDA', color: '#155724', label: 'נסגרה' },
};

/** Per-configuration mapping state, keyed by the backend validation code. */
export const WEBSITE_MAPPING_STATUS = {
  verified:                     { bg: '#D4EDDA', color: '#155724', label: 'מאומת'               },
  mapping_unverified:           { bg: '#FFF3CD', color: '#856404', label: 'ממתין לאימות'        },
  mapping_variant_archived:     { bg: '#F8D7DA', color: '#721C24', label: 'הווריאנט בארכיון'     },
  mapping_not_finished_product: { bg: '#F8D7DA', color: '#721C24', label: 'לא מוצר מוגמר'       },
  mapping_product_changed:      { bg: '#F8D7DA', color: '#721C24', label: 'הווריאנט עבר מוצר'   },
  unsaved:                      { bg: '#E2E3E5', color: '#383D41', label: 'לא נשמר'             },
};

// ── Supplier-based inventory allocation ─────────────────────────────────────────
// Values must stay in sync with backend/constants/statuses.js
// (ALLOCATION_SOURCE_TYPES) and backend/services/supplierInventory.js
// (SOURCE_LABELS). 'supplier' rows use the supplier's own name instead.
export const ALLOCATION_SOURCE_LABELS = {
  production:           'ייצור פנימי',
  opening:               'יתרת פתיחה',
  adjustment:            'התאמת מלאי',
  purchase_no_supplier:  'רכישה ללא ספק רשום',
};

/** Human label for one supplier-breakdown/allocation row. */
export const sourceRowLabel = (row) =>
  row.source_type === 'supplier'
    ? (row.supplier_name || `ספק #${row.supplier_id}`)
    : (ALLOCATION_SOURCE_LABELS[row.source_type] || row.source_type);

/** Returns inline style object for a <span> status badge */
export const badgeStyle = (meta, key) => {
  const m = meta[key] || { bg: '#eee', color: '#555' };
  return {
    backgroundColor: m.bg,
    color: m.color,
    padding: '3px 12px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };
};

// ── Typography helpers ────────────────────────────────────────────────────────
// (previously defined but not yet referenced anywhere — now completed and
// adopted on the pilot pages; see docs/design-guidelines.md §2.2)
export const typo = {
  pageTitle: { variant: 'h5', fontWeight: 700, color: NAVY },
  sectionTitle: { fontWeight: 700, color: NAVY, fontSize: 15 },
  id: { fontWeight: 600, color: 'text.secondary', fontSize: 13 },
  /** Secondary/meta line — the single most common ad hoc fontSize in the app */
  meta: { fontSize: 11, color: 'text.secondary' },
};

// ── Corner radius tokens ──────────────────────────────────────────────────────
// Names for the two sx `borderRadius` values already dominant in the app
// (see docs/design-guidelines.md §3.1) — additive only, does not change
// `card`/`btn`/`tbl`/`inputSx` above, which keep their own literal values.
export const radius = {
  control: 1, // inputs, chips — matches inputSx
  surface: 2, // cards, buttons, table containers — matches card/btn/tbl
};

// ── Shadow tokens ──────────────────────────────────────────────────────────────
// Names for the two shadow values already dominant in the app (see
// docs/design-guidelines.md §3.2) — additive only.
export const shadow = {
  resting: '0 2px 10px rgba(0,0,0,0.07)',
  hover:   '0 6px 18px rgba(0,0,0,0.12)',
};

// ── Responsive breakpoints ───────────────────────────────────────────────────
// The one breakpoint in the app — was duplicated as a literal `900` in both
// layout/CustomAppBar.js and layout/CustomMenu.js.
export const MOBILE_BREAKPOINT = 900;

// ── Layout helpers ────────────────────────────────────────────────────────────
export const layout = {
  /** Standard page header: title left, actions right */
  pageHeader: {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
  },
  /** Card grid for list pages */
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 2,
    p: 2,
    '@media (max-width:599.95px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 2,
      p: 0,
    },
  },
  /** Standard 2-column form field grid — the dominant shape already used
   *  across most Create/Edit pages' top field group */
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 2,
    width: '100%',
    '@media (max-width:599.95px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
};
